import Daily, { DailyCall, DailyEventObjectAppMessage } from '@daily-co/daily-js';
import { supabase, SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from '@/integrations/supabase/client';
import { inFunctionRegion } from '@/lib/function-region';

export class VoiceTestClient {
  private daily: DailyCall | null = null;
  private audioElement: HTMLAudioElement;
  private callLogId: string | null = null;
  private isConnected: boolean = false;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private boundHandlePageUnload: () => void;
  // The user's JWT, kept for the unload request: the tab is closing, so there is no time to
  // await supabase-js for it then.
  private accessToken: string | null = null;

  constructor(
    private onTranscript: (role: 'user' | 'agent', text: string, timestamp?: Date) => void,
    private onToolCall: (toolName: string, args: any, url?: string, method?: string, payload?: any) => void,
    private onToolResponse: (toolName: string, response: any) => void,
    private onToolError: (toolName: string, error: string) => void,
    private onError: (error: string) => void,
    private onConnectionChange: (connected: boolean) => void,
    private onCallEnded?: (reason: string, message: string) => void,
    private onThinkingStart?: () => void,
    private onThinkingText?: (content: string) => void,
    private onThinkingEnd?: () => void,
  ) {
    // Create audio element for bot audio
    this.audioElement = document.createElement('audio');
    this.audioElement.autoplay = true;

    // Bind the page unload handler
    this.boundHandlePageUnload = this.handlePageUnload.bind(this);
  }

  private ensureDailyInstance() {
    if (!this.daily) {
      console.log('🎥 Creating new Daily call object');
      this.daily = Daily.createCallObject({
        audioSource: true,
        startAudioOff: true, // Start muted, enable after connection confirmed
        videoSource: false,
      });
      this.attachDailyListeners(this.daily);
    }
    return this.daily;
  }

  // Listeners are attached once per Daily instance (not per connect()) so a
  // second call in the same dialog session doesn't stack duplicate handlers.
  private attachDailyListeners(daily: DailyCall) {
    daily
      .on('joined-meeting', async () => {
        console.log('✅ Joined Daily room');
        // Mark as connected - user can no longer "decline" before connection.
        // The call log is the bot's: it marks the call answered when it hears the user.
        this.isConnected = true;

        // Enable mic ONLY after connection confirmed
        await this.enableMicrophone();
        this.onConnectionChange(true);
        // Start heartbeat to keep call alive
        this.startHeartbeat();
        // Ensure microphone is enabled after joining
        setTimeout(() => {
          console.log('🎤 Ensuring microphone is enabled after joining');
          this.daily?.setLocalAudio(true);
        }, 100);
      })
      .on('left-meeting', () => {
        console.log('👋 Left Daily room');
        this.isConnected = false;
        this.stopHeartbeat();
        this.onConnectionChange(false);
      })
      .on('participant-joined', (event) => {
        console.log('👤 Participant joined:', event.participant.user_id);
        // Don't enable mic here anymore - wait for explicit confirmation
      })
      .on('track-started', (event) => {
        console.log('🎵 Track started:', event.participant?.user_id, event.track?.kind);
        // Handle bot audio track
        if (event.participant && event.track && event.track.kind === 'audio') {
          const stream = new MediaStream([event.track]);
          this.audioElement.srcObject = stream;
          console.log('🔊 Bot audio connected to audio element');
        }
      })
      .on('error', (error) => {
        console.error('❌ Daily error:', error);
        this.onError(error?.errorMsg || 'Daily connection error');
      })
      .on('app-message', (event: DailyEventObjectAppMessage) => {
        this.handleBotMessage(event.data);
      });
  }

  // Leaves and destroys the Daily instance without touching call_logs. Used
  // when the bot ends the call (backend already finalized the log) and before
  // re-joining, so the next connect() starts from a fresh call object.
  private teardownDaily() {
    this.stopHeartbeat();
    this.isConnected = false;
    this.callLogId = null;
    window.removeEventListener('beforeunload', this.boundHandlePageUnload);
    if (this.daily) {
      console.log('🧹 Tearing down Daily instance');
      this.daily.leave();
      this.daily.destroy();
      this.daily = null;
    }
  }

  // Pre-warm work that's safe to do before the user clicks Call:
  // - Force Daily to fetch + parse call-machine-object-bundle.js (~450ms off click path)
  // - Resolve the mic permission prompt early so the click path skips the prompt
  // Errors are swallowed; connect() will surface real errors at click time.
  async prewarm() {
    try {
      this.ensureDailyInstance();
    } catch (err) {
      console.warn('⚠️ Prewarm: failed to create Daily call object', err);
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      console.log('✅ Prewarm: mic permission resolved');
    } catch (err) {
      console.warn('⚠️ Prewarm: mic permission not granted yet', err);
    }
  }

  async connect(agentId: string, dynamicVariables?: Record<string, string>) {
    try {
      // A previous call in this session (e.g. ended by the agent) may have
      // left the instance in a joined/left state — join() would then fail
      // with "already joined meeting". Recreate from scratch in that case.
      if (this.daily && this.daily.meetingState() !== 'new') {
        console.log(`♻️ Daily instance in state "${this.daily.meetingState()}", recreating before join`);
        this.teardownDaily();
      }
      this.ensureDailyInstance();

      console.log('🎤 Requesting microphone permission...');

      // Request microphone permission first
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 24000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        // Stop the test stream immediately
        stream.getTracks().forEach(track => track.stop());
        console.log('✅ Microphone permission granted');
      } catch (micError) {
        console.error('❌ Microphone permission denied:', micError);
        throw new Error('Microphone permission is required for voice testing. Please allow microphone access and try again.');
      }

      console.log('🚀 Starting voice session for agent:', agentId);

      // Add beforeunload listener to handle tab close
      window.addEventListener('beforeunload', this.boundHandlePageUnload);
      await this.refreshAccessToken();

      // Call Supabase edge function to get Daily room
      const { data, error } = await supabase.functions.invoke('start-voice-session', {
        body: {
          agentId,
          ...(dynamicVariables && Object.keys(dynamicVariables).length > 0
            ? { dynamic_variables: dynamicVariables }
            : {}),
        }
      });

      if (error) {
        // Try to extract detailed error message from response
        let errorMessage = error.message || 'Unknown error';
        
        // Check for specific error types based on error context
        if (error.context?.status === 402 || errorMessage.includes('402')) {
          // Parse the error response body if available
          try {
            const errorBody = error.context?.body ? JSON.parse(error.context.body) : null;
            errorMessage = errorBody?.details || 'You need to top up your account to continue talking.';
          } catch {
            errorMessage = 'You need to top up your account to continue talking.';
          }
          throw new Error(errorMessage);
        }
        
        if (error.context?.status === 403) {
          try {
            const errorBody = error.context?.body ? JSON.parse(error.context.body) : null;
            errorMessage = errorBody?.details || 'Your account is currently paused. Please contact support.';
          } catch {
            errorMessage = 'Your account is currently paused. Please contact support.';
          }
          throw new Error(errorMessage);
        }
        
        // start-voice-session's only 404: the agent is gone.
        if (error.context?.status === 404) {
          throw new Error('This agent was deleted.');
        }

        // Handle capacity error with specific message
        if (errorMessage.includes('capacity') || error.context?.status === 429) {
          throw new Error('Voice service is at capacity. Please wait a moment and try again.');
        }
        
        throw new Error(`Failed to start session: ${errorMessage}`);
      }

      if (!data?.dailyRoom || !data?.dailyToken) {
        throw new Error('Invalid response from server: missing dailyRoom or dailyToken');
      }

      // Store call_log_id for potential user decline handling
      this.callLogId = data.call_log_id || null;
      console.log('✅ Got Daily room:', data.dailyRoom, 'call_log_id:', this.callLogId);

      // Join the Daily room (event listeners were attached at instance creation)
      await this.daily.join({
        url: data.dailyRoom,
        token: data.dailyToken,
      });

      // Ensure microphone is enabled after joining
      console.log('🎤 Enabling microphone after join');
      this.daily.setLocalAudio(true);

    } catch (error) {
      console.error('❌ Connection error:', error);
      this.isConnected = false;
      this.stopHeartbeat();
      window.removeEventListener('beforeunload', this.boundHandlePageUnload);
      
      // The session never connected: close its call log (the server ignores an answered call)
      if (this.callLogId) {
        await this.markCallEnded();
        this.callLogId = null;
      }
      
      this.onConnectionChange(false);
      // Re-throw the error so the caller can handle it with proper UI feedback
      throw error;
    }
  }

  private startHeartbeat() {
    // Send heartbeat every 30 seconds to indicate call is still active
    this.heartbeatInterval = setInterval(async () => {
      if (this.callLogId && this.isConnected) {
        try {
          await this.refreshAccessToken();
          await supabase.functions.invoke('web-call-heartbeat', {
            body: { call_log_id: this.callLogId }
          });
          console.log('💓 Heartbeat sent for call:', this.callLogId);
        } catch (err) {
          console.error('❌ Heartbeat failed:', err);
        }
      }
    }, 30000);
    console.log('💓 Heartbeat started');
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      console.log('💔 Heartbeat stopped');
    }
  }

  private async refreshAccessToken() {
    const { data } = await supabase.auth.getSession();
    this.accessToken = data.session?.access_token ?? null;
  }

  // The server only closes a call that was never answered; an answered one is the bot's to finalise.
  private async markCallEnded() {
    const { error } = await supabase.functions.invoke('mark-call-ended', {
      body: { call_log_id: this.callLogId },
    });
    if (error) console.error('❌ Failed to mark call as ended:', error);
  }

  private handlePageUnload() {
    // A keepalive fetch outlives the page and, unlike sendBeacon, carries the user's JWT, which
    // mark-call-ended requires.
    if (this.callLogId && this.accessToken) {
      console.log('🚪 Page unloading, marking call as ended');
      void fetch(inFunctionRegion(`${SUPABASE_URL}/functions/v1/mark-call-ended`), {
        method: 'POST',
        keepalive: true,
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          apikey: SUPABASE_PUBLISHABLE_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ call_log_id: this.callLogId }),
      }).catch(() => undefined);
    }
  }

  private handleBotMessage(data: any) {
    console.log('📨 Received bot message type:', data.type);

    // Unwrap server-message envelope
    if (data.type === 'server-message' && data.data) {
      console.log('📦 Unwrapping server-message:', data.data.type);
      data = data.data;
    }

    if (data.type === 'transcript') {
      console.log('✅ Processing transcript:', data.role, data.content, 'timestamp:', data.timestamp);
      // Pass timestamp if available from backend
      this.onTranscript(
        data.role === 'user' ? 'user' : 'agent', 
        data.content,
        data.timestamp ? new Date(data.timestamp) : undefined
      );
    } else if (data.type === 'tool_call') {
      console.log('✅ Processing tool call:', data.toolName, data);
      this.onToolCall(data.toolName, data.args, data.url, data.method, data.payload);
    } else if (data.type === 'tool_response') {
      console.log('✅ Processing tool response:', data.toolName);
      this.onToolResponse(data.toolName, data.response);
    } else if (data.type === 'tool_error') {
      console.log('✅ Processing tool error:', data.toolName);
      this.onToolError(data.toolName, data.error);
    } else if (data.type === 'error') {
      console.log('✅ Processing error:', data.message);
      this.onError(data.message);
    } else if (data.type === 'call_ended') {
      console.log('📞 Call ended:', data.reason, data.message);
      if (this.onCallEnded) {
        this.onCallEnded(data.reason || 'unknown', data.message || 'Call ended');
      }
      // Also update connection status
      this.onConnectionChange(false);
      // Leave and destroy the Daily instance so a subsequent connect() joins
      // the new room instead of hitting "already joined meeting". Deferred so
      // we don't destroy the instance from inside its own event callback.
      setTimeout(() => this.teardownDaily(), 0);
    } else if (data.type === 'thinking_start') {
      console.log('🧠 Thinking started');
      this.onThinkingStart?.();
    } else if (data.type === 'thinking_text') {
      console.log('🧠 Thinking:', data.content?.slice(0, 80));
      if (data.content) this.onThinkingText?.(data.content);
    } else if (data.type === 'thinking_end') {
      console.log('🧠 Thinking ended');
      this.onThinkingEnd?.();
    } else if (data.type === 'iframe-pos') {
      // Silently ignore iframe-pos messages
    }
  }

  private async enableMicrophone() {
    try {
      if (this.daily) {
        console.log('🎤 Enabling microphone after connection confirmed');
        await this.daily.setLocalAudio(true);
      }
    } catch (error) {
      console.error('❌ Error enabling microphone:', error);
      this.onError('Failed to enable microphone');
    }
  }

  async disconnect() {
    // Stop heartbeat first
    this.stopHeartbeat();
    
    // Remove page unload listener
    window.removeEventListener('beforeunload', this.boundHandlePageUnload);

    // Always tell the server when disconnect is called: early close, close during the API
    // call, close during the Daily transfer. It ends only a call nobody answered.
    if (this.callLogId) {
      console.log(`👋 Disconnect called, isConnected=${this.isConnected}, marking call as ended`);
      await this.markCallEnded();
    }

    this.teardownDaily();
    this.onConnectionChange(false);
  }
}
